<template>
  <transition tag="div" name="modal">
    <div
      class="modals"
      v-show="modals.length"
      :key="modals.length"
      tabindex="0"
      @keyup.esc.prevent="handleEscapeKey($event)"
      @click="handleBackdropClick"
    >
      <div class="modals__wrap">
        <div
          class="modal"
          v-for="(modal, index) in modals"
          :key="index"
          :class="getCssClasses(index)"
        >
          <ModalCard
            :closable="modal.closable"
            :title="modal.title || modal.titled"
            @close="close"
          >
            <component
              :is="modal.component"
              v-bind="modal.props"
              ref="components"
            />
          </ModalCard>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import ModalCard from "./ModalCard";

import Bus from "./bus";

export default {
  name: "modals",

  components: {
    ModalCard
  },

  data() {
    return {
      modals: []
    };
  },

  computed: {
    current() {
      return this.modals[this.$last];
    },
    $last() {
      return this.modals.length - 1;
    },
    html() {
      if (typeof document !== "undefined") {
        return document.querySelector("html");
      }
      return null;
    }
  },

  watch: {
    "modals.length"(number) {
      if (number > 0) {
        this.html.classList.add("modal-open");
      } else {
        this.html.classList.remove("modal-open");
      }
    }
  },

  created() {
    Bus.$on("new", this.new);
    Bus.$on("close", this.close);
    Bus.$on("dismiss", this.dismiss);
  },

  destroyed() {
    Bus.$off("new", this.new);
    Bus.$off("close", this.close);
    Bus.$off("dismiss", this.dismiss);
  },

  methods: {
    new(options) {
      const defaults = {
        title: null,
        titled: true,
        dismissable: true,
        closable: true,
        name: "",
        escapable: false,
        closeOnBackdrop: true,
        onClose() {},
        onDismiss() {}
      };
      options = Object.assign(defaults, options);

      this.modals.push(options);
      Bus.$emit("opened", {
        index: this.$last,
        options
      });

      document.querySelector(".modals").scrollTop = 0;
      if (
        document.activeElement &&
        document.activeElement.tagName !== "BODY" &&
        document.activeElement.blur
      ) {
        document.activeElement.blur();
      }
    },
    splice(index = null) {
      if (index === -1) return;
      if (!this.modals.length) return;
      if (index === null) this.modals.pop();
      else this.modals.splice(index, 1);
      // If it was the last window, notify that all instances are destroyed
      if (!this.modals.length) {
        Bus.$emit("destroyed");
      }
    },

    doClose(data = null, index) {
      // If there's nothing to close, ignore it
      try {
        if (!this.modals.length) return;
        if (!this.modals[index]) return;
        this.splice(index);
        // Firefox fix: https://github.com/javisperez/modals/issues/1
        const modals = document.querySelector(".modals");
        if (modals) modals.scrollTop = 0;
      } catch (e) {
        console.warn("modals doClose exception:", e, data);
      }
    },

    getIndexByComponent(component) {
      if (component._isVue) {
        const modals = Array.from(this.$refs.components.entries());
        const pair = modals.find(item => item[1] === component);
        return pair ? pair[0] : -1;
      } else {
        return this.modals.findIndex(
          ({ component: item }) => item && item.name === component.name
        );
      }
    },

    getIndex({ index = -1, component = null }) {
      if (component) index = this.getIndexByComponent(component);
      if (index === -1) index = this.$last;
      return index;
    },

    close(data = {}) {
      if (this.modals.length === 0) return;

      const index = this.getIndex(data);

      const modal = this.modals[index];

      modal.onClose(data);
      this.doClose(data, index);

      Bus.$emit("closed", {
        index,
        instance: modal,
        data
      });
    },

    dismiss(index = this.$last) {
      // Can't dismiss modal if there's no modal open
      if (this.modals.length === 0) return;

      if (!this.modals[index].dismissable) return;

      // Check dismiss callback result for prevention
      if (this.modals[index].onDismiss() === false) return;

      this.doClose(null, index);

      Bus.$emit("dismissed", {
        index,
        instance: this.modals[index]
      });
    },

    getCssClasses(index) {
      const modal = this.modals[index];
      let classNames = modal.name;
      if (index < this.$last) classNames += " disabled";
      return classNames;
    },
    handleEscapeKey() {
      if (!this.modals.length) return;
      if (this.current.escapable) this.dismiss();
    },
    handleBackdropClick() {
      if (!this.modals.length) {
        return;
      }
      if (this.current.closeOnBackdrop === true) {
        this.dismiss();
      }
    }
  }
};
</script>

<style lang="scss">
html.modal-open {
  overflow: hidden;
  height: 100%;
}
.modals {
  background-color: rgba(#001e46, 0.8);
  position: fixed;
  left: 0;
  right: 0;
  z-index: 1050;
  overflow-x: hidden;
  overflow-y: auto;
  perspective: 500px;
  transition: 0.4s ease;
  transition-property: opacity;
  top: 0;
  min-height: 100%;

  &:focus {
    outline: none;
  }
}

.modals__wrap {
  background: transparent;
  box-shadow: none;
  transition: 0.6s ease;
  transition-property: transform, opacity;
  position: absolute;
  height: 100vh;
  width: 100vw;

  .simplebar-content {
    height: 100%;
    position: relative;
  }

  .simplebar-scrollbar {
    width: 4px;

    .simplebar-track.simplebar-horizontal & {
      height: 4px;
    }

    &:before {
      border-radius: 100px;
      background: rgba(#fff, 0.5);
    }
  }
}

.modal {
  margin: 0 auto;
  padding: 55px 0 30px;
  width: 100%;
  max-width: 95%;
  box-sizing: border-box;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  transition: 0.6s ease;
  transition-property: transform, opacity;
  will-change: transform;
  display: flex;
  align-items: center;
  min-height: 100%;

  &.disabled {
    opacity: 0.2;
    max-height: 100%;
    overflow: hidden;

    &::after {
      background: transparent;
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 100;
    }
  }
}
.modal-enter,
.modal-leave-active {
  opacity: 0;
}
.modal-enter .modal,
.modal-leave-active .modal {
  opacity: 0;
  transform: translateX(-50%) translateY(-30px) scale(0.95);
}

/* .modal-open {
  .application_wrap {
    @include filter-blur(3);
  }
} */
</style>
